import { useNotifications, NotificationType, NotificationPriority } from "@/contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { Bell, Check, X, AlertTriangle, ShieldCheck, Package, FileText, CreditCard, Truck, Info, CheckCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
  gst_deadline: <ShieldCheck className="h-4 w-4" />,
  low_inventory: <Package className="h-4 w-4" />,
  unpaid_invoice: <FileText className="h-4 w-4" />,
  subscription_renewal: <CreditCard className="h-4 w-4" />,
  eway_expiry: <Truck className="h-4 w-4" />,
  system: <Info className="h-4 w-4" />,
  approval: <Check className="h-4 w-4" />,
};

const PRIORITY_COLORS: Record<NotificationPriority, string> = {
  critical: "text-destructive",
  high: "text-orange-500",
  medium: "text-primary",
  low: "text-muted-foreground",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const { notifications, unreadCount, criticalCount, markAsRead, markAllAsRead, dismiss } = useNotifications();
  const navigate = useNavigate();

  const visible = notifications.filter((n) => !n.isDismissed).slice(0, 10);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative text-muted-foreground hover:text-foreground transition-colors p-2 rounded-lg hover:bg-muted/60">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold text-primary-foreground ${
              criticalCount > 0 ? "bg-destructive animate-pulse" : "bg-primary"
            }`}>
              {unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && <Badge variant="secondary" className="text-[10px] h-5">{unreadCount} new</Badge>}
          </div>
          <div className="flex gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={markAllAsRead}>
                <CheckCheck className="h-3 w-3" /> Read All
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate("/dashboard/notifications")}>
              View All
            </Button>
          </div>
        </div>
        <ScrollArea className="max-h-[380px]">
          {visible.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No notifications</div>
          ) : (
            visible.map((n) => (
              <div
                key={n.id}
                className={`flex items-start gap-3 px-4 py-3 border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer ${
                  !n.isRead ? "bg-primary/5" : ""
                }`}
                onClick={() => {
                  markAsRead(n.id);
                  if (n.actionUrl) navigate(n.actionUrl);
                }}
              >
                <div className={`mt-0.5 shrink-0 ${PRIORITY_COLORS[n.priority]}`}>
                  {TYPE_ICONS[n.type]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm leading-tight ${!n.isRead ? "font-semibold text-foreground" : "text-foreground/80"}`}>
                      {n.title}
                    </p>
                    <button
                      onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-muted-foreground">{timeAgo(n.createdAt)}</span>
                    {n.priority === "critical" && (
                      <Badge variant="destructive" className="text-[9px] h-4 gap-0.5">
                        <AlertTriangle className="h-2.5 w-2.5" /> Critical
                      </Badge>
                    )}
                    {n.channels.includes("email") && <Badge variant="outline" className="text-[9px] h-4">Email</Badge>}
                    {n.channels.includes("mobile") && <Badge variant="outline" className="text-[9px] h-4">Mobile</Badge>}
                  </div>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
